import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Globe, Send, CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send message');
      }

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', subject: '', message: '' });
      }, 5000);
    } catch (err) {
      console.error('Contact form error:', err);
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      details: ['admissions@gihds.org']
    },
    {
      icon: Globe,
      title: 'Website',
      details: ['www.gihds.org']
    }
  ];

  const inquiryTypes = [
    'General Inquiry',
    'Admissions Question',
    'Scholarship Information',
    'Program Details',
    'Technical Support',
    'Other'
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Get in Touch
            </p>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Contact Us
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Have questions about our program, admissions, or scholarship opportunities? 
              We're here to help. Reach out to our team and we'll get back to you promptly.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#F1F5F8] p-6 md:p-8"
              >
                <div className="w-12 h-12 bg-[#163A59]/10 text-[#163A59] flex items-center justify-center rounded-sm mb-4">
                  <item.icon size={24} />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                {item.details.map((detail) => (
                  <p key={`detail-${detail.slice(0, 10)}`} className="text-slate-600 text-sm">{detail}</p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Send a Message
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                We'd Love to Hear From You
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Whether you have questions about the application process, need clarification 
                on program details, or want to learn more about scholarship opportunities, 
                our team is ready to assist you.
              </p>
              <div className="bg-white p-6 border-l-4 border-[#163A59]">
                <h4 className="font-semibold text-slate-900 mb-2">Response Time</h4>
                <p className="text-slate-600 text-sm">
                  We aim to respond to all inquiries within 24-48 hours during business days. 
                  For urgent matters, please indicate this in your subject line.
                </p>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 md:p-10 border border-slate-200"
            >
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 text-green-600 flex items-center justify-center rounded-full mx-auto mb-6">
                    <CheckCircle size={32} />
                  </div>
                  <h3 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">
                    Message Sent!
                  </h3>
                  <p className="text-slate-600">
                    Thank you for reaching out. We'll get back to you within 24-48 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#163A59] focus:border-transparent transition-all"
                      placeholder="Your full name"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#163A59] focus:border-transparent transition-all"
                      placeholder="your.email@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#163A59] focus:border-transparent transition-all"
                      data-testid="contact-subject-select"
                    >
                      <option value="">Select a subject</option>
                      {inquiryTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#163A59] focus:border-transparent transition-all resize-none"
                      placeholder="How can we help you?"
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm flex items-center gap-2">
                      <AlertCircle size={18} />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#163A59] hover:bg-[#102A43] disabled:bg-slate-400 text-white px-8 py-4 rounded-sm font-medium transition-all"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Global Reach Section */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold mb-6">
              Global Reach
            </h2>
            <p className="text-slate-400 leading-relaxed mb-6">
              Our program is delivered 100% online, allowing students from around the world 
              to access quality humanitarian education without geographical barriers.
            </p>
            <p className="text-slate-400 leading-relaxed">
              We stay connected to the latest developments in humanitarian policy and practice, 
              ensuring our curriculum remains relevant and cutting-edge.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
